// Moments Tab Container
//
// Grid of profile icons for everyone with an active Moment; tapping one opens
// the viewer on that person's clips. Reels arrive from /feed/moments already
// ranked (unwatched authors first, then affinity and freshness — see
// get_moments_feed in migration 042), so this renders them in the order given
// and never re-sorts.
//
// When a person's Moments finish, the viewer advances into the next reel rather
// than closing. That chain is the difference between watching one person and
// keeping going, and it is why the ordering above matters at all.

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Story } from "../../Profile/types";
import { StoryViewer } from "../../Profile/Stories";
import { momentsService, MomentReel } from "../../../services/SocialFeed/momentsService";
import { feedCacheKey, readFeedCache, writeFeedCache } from "../../../services/SocialFeed/feedCache";
import { startLoop } from "../../../services/SocialFeed/feedLoop";
import { logger } from "../../../utils/logger";
import { ProfilePeekDrawer, useProfilePeek } from "../../shared/ProfilePeekDrawer";
import { signalsService } from "../../../services/SocialFeed/signalsService";
import { FeedToast } from "../../../styles/SocialFeed/MatchesTab.styles";
import {
  StoriesGridContainer,
  StoriesGrid,
  StoryProfileIcon,
  StoryProfileItem,
  StoryProfileName,
  StoriesEmptyState,
  StoriesEmptyStateIcon,
  StoriesEmptyStateTitle,
  StoriesEmptyStateDescription,
} from "../../../styles/SocialFeed/StoriesTab.styles";

const AVATAR_FALLBACK =
  'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80"><rect width="80" height="80" fill="%23ddd"/><text x="50%25" y="50%25" text-anchor="middle" dy=".3em" font-size="24">👤</text></svg>';

interface StoriesTabContainerProps {
  userId: string;
  onClose?: () => void; // Callback to switch tabs when moments are closed
  onViewProfile?: (profileUserId: string) => void; // Callback to view user profile
}

export const StoriesTabContainer: React.FC<StoriesTabContainerProps> = ({
  userId,
  onClose,
  onViewProfile,
}) => {
  // What this tab last held, if still fresh. Read once at mount.
  const [cached] = useState(() =>
    readFeedCache<MomentReel>(feedCacheKey('stories', userId))
  );

  const [reels, setReels] = useState<MomentReel[]>(cached?.items ?? []);
  const [isLoading, setIsLoading] = useState(!cached);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Index into `reels` rather than a user id, because advancing through the
  // chain is a walk along this list.
  const [openReelIndex, setOpenReelIndex] = useState<number | null>(null);
  // Locally-watched Moments, so the ring dims immediately instead of waiting
  // for the next fetch to reflect it.
  const [seenIds, setSeenIds] = useState<Set<string>>(new Set());
  const { peekTarget, isPeekOpen, openPeek, closePeek, toast, showToast } = useProfilePeek('moments');

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        signalsService.track({ signalType: 'feed_open', surface: 'moments' });
        // Already warm, from an earlier visit or from the prefetcher.
        if (cached) return;
        const data = await momentsService.getMomentsFeed();
        if (!cancelled) {
          setReels(data);
          // Moments has no paging, so the cursor is inert. Stored anyway to
          // keep every cache entry the same shape.
          writeFeedCache(feedCacheKey('stories', userId), {
            items: data,
            cursor: startLoop('moments'),
          });
        }
      } catch (error: any) {
        if (!cancelled) {
          logger.error("[MOMENTS] Failed to load moments feed:", error);
          // Show what actually went wrong. A generic message here sent the last
          // debugging session hunting through the backend for a fault that was
          // really an expired token.
          setLoadError(error?.message || "Moments could not be loaded. Try again shortly.");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [userId]);

  // Second line of defence behind the backend's 72-hour window: a tab left
  // open across the boundary shouldn't keep showing what has since expired.
  const activeReels = useMemo(() => {
    const now = Date.now();
    return reels
      .map((reel) => ({
        ...reel,
        moments: reel.moments.filter((m) => m.expiresAt.getTime() > now),
      }))
      .filter((reel) => reel.moments.length > 0);
  }, [reels]);

  const openReel = activeReels[openReelIndex ?? -1] ?? null;

  const handleProfileIconClick = (index: number) => setOpenReelIndex(index);

  const handleClose = () => setOpenReelIndex(null);

  /**
   * A Moment finished playing. Recording the view is what demotes its author
   * out of the unwatched-first band on the next load.
   */
  const handleMomentViewed = useCallback((moment: Story) => {
    setSeenIds((prev) => {
      if (prev.has(moment.id)) return prev;
      const next = new Set(prev);
      next.add(moment.id);
      return next;
    });
    momentsService.markViewed(moment.id);
    // Recorded in the engagement log as well as moment_views: the latter is
    // feed state (has this been seen), this is history (who watches whom).
    signalsService.track({
      signalType: 'moment_view',
      targetUserId: moment.userId,
      surface: 'moments',
      itemId: moment.id,
    });
  }, []);

  /**
   * End of this person's Moments. Continue into the next reel instead of
   * closing; only the genuine end of the list returns to the grid.
   */
  const handleNextReel = useCallback(() => {
    setOpenReelIndex((current) => {
      if (current === null) return null;
      // Reaching the end of someone's reel means every clip was watched, which
      // says more than any single view.
      const finished = activeReels[current];
      if (finished) {
        signalsService.track({
          signalType: 'moment_complete',
          targetUserId: finished.userId,
          surface: 'moments',
          metadata: { momentCount: finished.momentCount },
        });
      }
      return current + 1 < activeReels.length ? current + 1 : null;
    });
  }, [activeReels]);

  // Going back past the very first reel returns to the grid, matching what
  // tapping back used to do at the start of a sequence.
  const handlePrevReel = useCallback(() => {
    setOpenReelIndex((current) => {
      if (current === null) return null;
      return current > 0 ? current - 1 : null;
    });
  }, []);

  const getUserName = useCallback(
    (targetUserId: string) =>
      activeReels.find((reel) => reel.userId === targetUserId)?.userName ?? "Someone",
    [activeReels]
  );

  // Opens the profile over the reel instead of navigating. Leaving the feed
  // here was the most disruptive of the three: it tore down a playing Moment
  // and dropped the viewer's place in the queue entirely.
  const handleSwipeUp = useCallback(
    (profileUserId: string) => {
      if (onViewProfile) {
        onViewProfile(profileUserId);
        return;
      }
      const reel = activeReels.find((r) => r.userId === profileUserId);
      openPeek({
        profileId: profileUserId,
        firstName: reel?.userName || getUserName(profileUserId),
        context: reel
          ? `${reel.momentCount} moment${reel.momentCount === 1 ? '' : 's'} today`
          : 'From Moments',
      });
    },
    [activeReels, getUserName, onViewProfile, openPeek]
  );

  if (isLoading) {
    return (
      <StoriesEmptyState>
        <StoriesEmptyStateIcon>⏳</StoriesEmptyStateIcon>
        <StoriesEmptyStateTitle>Loading moments…</StoriesEmptyStateTitle>
      </StoriesEmptyState>
    );
  }

  if (loadError) {
    return (
      <StoriesEmptyState>
        <StoriesEmptyStateIcon>⚠️</StoriesEmptyStateIcon>
        <StoriesEmptyStateTitle>Couldn't load moments</StoriesEmptyStateTitle>
        <StoriesEmptyStateDescription>{loadError}</StoriesEmptyStateDescription>
      </StoriesEmptyState>
    );
  }

  if (activeReels.length === 0) {
    return (
      <StoriesEmptyState>
        <StoriesEmptyStateIcon>📸</StoriesEmptyStateIcon>
        <StoriesEmptyStateTitle>No moments available</StoriesEmptyStateTitle>
        <StoriesEmptyStateDescription>
          Moments disappear 72 hours after they're posted — check back soon for new ones.
        </StoriesEmptyStateDescription>
      </StoriesEmptyState>
    );
  }

  return (
    <>
      {openReelIndex === null && (
        <StoriesGridContainer>
          <StoriesGrid>
            {activeReels.map((reel, index) => {
              // A filled ring means there is something here you haven't seen.
              const hasUnwatched =
                reel.moments.some((m) => !m.viewedByMe && !seenIds.has(m.id));
              return (
                <StoryProfileItem
                  key={reel.userId}
                  onClick={() => handleProfileIconClick(index)}
                  aria-label={`View ${reel.userName}'s moments`}
                >
                  <StoryProfileIcon $hasStories={hasUnwatched}>
                    <img
                      src={reel.avatar || AVATAR_FALLBACK}
                      alt={`${reel.userName}'s profile`}
                      onError={(e) => { e.currentTarget.src = AVATAR_FALLBACK; }}
                    />
                  </StoryProfileIcon>
                  <StoryProfileName>{reel.userName}</StoryProfileName>
                </StoryProfileItem>
              );
            })}
          </StoriesGrid>
        </StoriesGridContainer>
      )}

      {openReel && (
        <StoryViewer
          // Remounting per reel resets playback and progress state cleanly,
          // which is what makes advancing through the chain behave like opening
          // a fresh viewer rather than mutating the current one.
          key={openReel.userId}
          isOpen
          stories={openReel.moments}
          initialIndex={0}
          onClose={handleClose}
          userId={openReel.userId}
          userName={openReel.userName}
          onSwipeUp={handleSwipeUp}
          // Both the swipe-up gesture and the name/avatar tap open the drawer,
          // rather than one navigating and the other not.
          onProfileClick={handleSwipeUp}
          getUserName={getUserName}
          onMomentViewed={handleMomentViewed}
          // Hold the reel while the profile is open, so it doesn't advance or
          // auto-close behind the drawer.
          paused={isPeekOpen}
          onNextUser={handleNextReel}
          onPrevUser={handlePrevReel}
          hasNextUser={openReelIndex !== null && openReelIndex + 1 < activeReels.length}
        />
      )}

      {toast && <FeedToast role="status">{toast}</FeedToast>}

      {/* Rendered outside the viewer block so a profile opened from the grid
          works too, not only one opened mid-reel. The drawer's z-index (10021)
          puts it above the fullscreen viewer (10002). */}
      <ProfilePeekDrawer
        target={peekTarget}
        onClose={closePeek}
        backLabel="Moments"
        onNotify={(_type, message) => showToast(message)}
      />
    </>
  );
};
